import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Instagram, Linkedin, Check } from "lucide-react";
import { SectionBadge, IconMail, Highlight, FadeUp } from "@/components/BrandKit";
import { DoodleStar } from "@/components/Doodles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIALS = [
  { label: "Instagram", handle: "@thebigbearstudios", href: "https://www.instagram.com/thebigbearstudios/", icon: Instagram },
  { label: "LinkedIn", handle: "/bigbearstudios", href: "https://www.linkedin.com/company/bigbearstudios/", icon: Linkedin },
];

// Field defs for the two-step form
const FIELDS_STEP_1 = [
  { key: "name", label: "01", question: "What should we call you?", placeholder: "Your name", type: "text", required: true },
  { key: "email", label: "02", question: "Your internet address?", placeholder: "Your email ID", type: "email", required: true },
  { key: "company", label: "03", question: "Where do you work?", placeholder: "Your company", type: "text", required: false },
];

function TwoStepForm() {
  const [step, setStep] = useState(1); // 1, 2, or 3 (done)
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const firstFieldRef = useRef(null);
  const messageRef = useRef(null);

  useEffect(() => {
    if (step === 1) firstFieldRef.current?.focus();
    if (step === 2) messageRef.current?.focus();
  }, [step]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validateStep1 = () => {
    if (!form.name.trim()) {
      toast.error("A name would be lovely.");
      return false;
    }
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("That email looks a little off - mind checking?");
      return false;
    }
    return true;
  };

  const goNext = () => {
    if (!validateStep1()) return;
    setStep(2);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.message.trim()) {
      toast.error("Give us a line or two - we're excited to hear.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setStep(3);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setForm({ name: "", email: "", company: "", message: "" });
    setStep(1);
  };

  const totalSteps = 2;
  const progress = step >= 3 ? 100 : ((step - 1) / totalSteps) * 100 + 25;

  return (
    <form
      data-testid="contact-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (step === 1) goNext();
        else if (step === 2) submit();
      }}
      className="relative border border-black bg-white p-8 sm:p-10 lg:p-12 min-h-[460px]"
      style={{ boxShadow: "10px 10px 0 rgba(0,0,0,0.9)" }}
    >
      {/* Progress bar */}
      <div className="absolute left-0 top-0 h-1 w-full bg-black/10 overflow-hidden">
        <motion.div
          className="h-full bg-[#FF4D00]"
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>

      {/* Corner tag */}
      <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 bg-black text-white px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em]">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#48B02C] animate-pulse" />
        {step === 3 ? "Brief received" : `Step ${step} of ${totalSteps}`}
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div
            key="step-1"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col gap-8 mt-2"
          >
            {FIELDS_STEP_1.map((f, i) => (
              <div key={f.key} className="flex flex-col gap-2">
                <div className="flex items-baseline gap-3">
                  <span
                    className="inline-flex items-center justify-center min-w-[36px] h-9 px-2 border border-black bg-[#FF4D00] text-white font-display text-xs"
                    style={{ fontWeight: 900 }}
                  >
                    {f.label}
                  </span>
                  <label
                    htmlFor={`f-${f.key}`}
                    className="font-display uppercase text-lg sm:text-xl leading-[1.15] tracking-tight"
                    style={{ fontWeight: 800 }}
                  >
                    {f.question}
                  </label>
                </div>
                <input
                  id={`f-${f.key}`}
                  data-testid={`contact-${f.key}`}
                  ref={i === 0 ? firstFieldRef : null}
                  type={f.type}
                  value={form[f.key]}
                  onChange={update(f.key)}
                  placeholder={f.placeholder}
                  className="w-full bg-transparent border-b-2 border-black focus:border-[#FF4D00] outline-none pb-2 pt-1 text-lg sm:text-xl font-display tracking-tight placeholder:text-[#5C5C5C]/50 transition-colors"
                  style={{ fontWeight: 600 }}
                />
              </div>
            ))}

            <button
              type="button"
              data-testid="contact-next"
              onClick={goNext}
              className="group mt-2 self-start inline-flex items-center gap-3 bg-black text-[#fdfbf7] px-8 h-14 font-grotesk uppercase tracking-[0.18em] text-[12px] hover:bg-[#FF4D00] transition-colors"
            >
              <span>Next: Enter Brief</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div
            key="step-2"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col gap-6 mt-2"
          >
            <div className="flex items-baseline gap-3">
              <span
                className="inline-flex items-center justify-center w-9 h-9 border border-black bg-[#FF4D00] text-white font-display text-xs"
                style={{ fontWeight: 900 }}
              >
                04
              </span>
              <label
                htmlFor="f-message"
                className="font-display uppercase text-2xl sm:text-3xl lg:text-[36px] leading-[1.1] tracking-tight"
                style={{ fontWeight: 800 }}
              >
                What are you building?
              </label>
            </div>
            <textarea
              id="f-message"
              data-testid="contact-message"
              ref={messageRef}
              rows={5}
              value={form.message}
              onChange={update("message")}
              placeholder="Tell us the brief"
              className="w-full resize-none bg-transparent border-b-2 border-black focus:border-[#FF4D00] outline-none pb-3 pt-1 text-lg sm:text-xl font-display tracking-tight placeholder:text-[#5C5C5C]/50 transition-colors"
              style={{ fontWeight: 600 }}
            />
            <div className="flex items-center gap-4 flex-wrap">
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="group inline-flex items-center gap-3 bg-black text-[#fdfbf7] px-8 h-14 font-grotesk uppercase tracking-[0.18em] text-[12px] hover:bg-[#FF4D00] transition-colors disabled:opacity-60"
              >
                <span>{loading ? "Sending..." : "Submit"}</span>
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-12" />
              </button>
              <button
                type="button"
                data-testid="contact-back"
                onClick={() => setStep(1)}
                className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C5C5C] hover:text-black transition-colors"
              >
                ← Back
              </button>
            </div>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div
            key="done"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col gap-5 py-6"
          >
            <div className="inline-flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-10 h-10 bg-[#48B02C] text-white">
                <Check className="w-5 h-5" strokeWidth={3} />
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C5C5C]">
                Delivered
              </span>
            </div>
            <h3
              data-testid="contact-thanks"
              className="font-display uppercase text-3xl sm:text-4xl lg:text-[42px] leading-[1.05] tracking-tight"
              style={{ fontWeight: 900 }}
            >
              Thanks for reaching out!
            </h3>
            <p className="text-[#3a3a3a] text-base sm:text-lg leading-[1.6] max-w-md">
              We&apos;ll be in touch soon.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <button
                type="button"
                onClick={reset}
                className="inline-flex items-center gap-2 border border-black px-5 h-12 font-grotesk uppercase tracking-[0.16em] text-[12px] hover:bg-black hover:text-white transition-colors"
              >
                Send another
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Step pips */}
      {step < 3 && (
        <div className="mt-10 pt-6 border-t border-black/10 flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C5C5C]">
            {step === 1 ? "" : "The brief"}
          </span>
          <div className="flex gap-1.5">
            {[1, 2].map((n) => (
              <span
                key={n}
                className={`w-6 h-1 transition-colors ${n <= step ? "bg-[#FF4D00]" : "bg-black/15"}`}
              />
            ))}
          </div>
        </div>
      )}
    </form>
  );
}

export default function Footer() {
  return (
    <footer
      id="contact"
      data-testid="footer-section"
      className="bg-[#FDFBF7] text-black relative"
    >
      {/* Contact */}
      <div className="border-t border-black">
        <div className="max-w-[1480px] mx-auto px-5 sm:px-8 py-20 sm:py-24">
          <div className="grid grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="col-span-12 lg:col-span-5 relative">
              <FadeUp>
                <SectionBadge
                  label="Contact"
                  num="06"
                  color="#FF4D00"
                  icon={<IconMail className="w-4 h-4 text-white" />}
                />
              </FadeUp>
              <FadeUp delay={0.08}>
                <h2
                  data-testid="footer-heading"
                  className="mt-5 font-display uppercase text-4xl sm:text-5xl lg:text-[64px] leading-[0.95] tracking-[-0.03em]"
                  style={{ fontWeight: 900 }}
                >
                  Bring us a <Highlight color="#FF4D00">brief</Highlight>.
                </h2>
              </FadeUp>
              <FadeUp delay={0.15}>
                <p className="mt-5 max-w-md text-[#5C5C5C] text-base sm:text-lg leading-[1.6]">
                  A campaign, a film, a launch, a strange idea scribbled in your
                  Notes app. We&apos;re listening.
                </p>
              </FadeUp>

              <div className="mt-10 space-y-5">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C5C5C] mb-2">
                    Reach out
                  </p>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-x-4 gap-y-1 flex-wrap">
                    <a
                      data-testid="footer-email-cj"
                      href="mailto:cj@bigbearstudios.in"
                      className="font-display text-lg sm:text-xl tracking-tight hover:text-[#FF4D00] transition-colors"
                      style={{ fontWeight: 800 }}
                    >
                      cj@bigbearstudios.in
                    </a>
                    <span className="hidden sm:inline text-[#5C5C5C]">|</span>
                    <a
                      data-testid="footer-email-m"
                      href="mailto:m@bigbearstudios.in"
                      className="font-display text-lg sm:text-xl tracking-tight hover:text-[#FF4D00] transition-colors"
                      style={{ fontWeight: 800 }}
                    >
                      m@bigbearstudios.in
                    </a>
                  </div>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C5C5C] mb-1">
                    Studio
                  </p>
                  <p
                    className="font-display text-xl sm:text-2xl tracking-tight"
                    style={{ fontWeight: 800 }}
                  >
                    Bengaluru / Mumbai
                  </p>
                </div>
              </div>

              <motion.div
                animate={{ y: [0, -8, 0], rotate: [0, -10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="hidden lg:block absolute -bottom-24 right-6 text-[#FFB703]"
              >
                <DoodleStar className="w-14 h-14" />
              </motion.div>
            </div>

            <div className="col-span-12 lg:col-span-7 relative">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              >
                <TwoStepForm />
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div className="bg-black text-[#fdfbf7]">
        <div className="max-w-[1480px] mx-auto px-5 sm:px-8 pt-16 pb-8">
          <div className="grid grid-cols-12 gap-8 items-end pb-10 border-b border-[#fdfbf7]/20">
            <div className="col-span-12 lg:col-span-8">
              <motion.p
                data-testid="footer-mega-wordmark"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9 }}
                className="font-display uppercase leading-[0.88] tracking-[-0.04em] text-6xl sm:text-8xl lg:text-[152px]"
                style={{ fontWeight: 900 }}
              >
                Big Bear<br />Studios<span className="text-[#FF4D00]">.</span>
              </motion.p>
              <p
                className="mt-5 font-display text-lg sm:text-xl text-[#fdfbf7]/70 tracking-tight"
                style={{ fontWeight: 600 }}
              >
                We&apos;re serious about making fun films.
              </p>
            </div>

            <div className="col-span-12 lg:col-span-4">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#fdfbf7]/60 mb-5">
                ◆ Find us in the wild
              </p>
              <ul data-testid="socials-list" className="space-y-3">
                {SOCIALS.map((s) => {
                  const Icon = s.icon;
                  return (
                    <li key={s.label}>
                      <a
                        data-testid={`social-${s.label.toLowerCase()}`}
                        href={s.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center justify-between gap-4 py-3 border-b border-[#fdfbf7]/15 hover:border-[#FF4D00] transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="w-5 h-5" strokeWidth={1.5} />
                          <span
                            className="font-display uppercase text-lg tracking-tight"
                            style={{ fontWeight: 800 }}
                          >
                            {s.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#fdfbf7]/50 hidden sm:inline">
                            {s.handle}
                          </span>
                          <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </div>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <div className="pt-6 flex flex-wrap items-center justify-between gap-3 font-mono text-[10px] uppercase tracking-[0.22em] text-[#fdfbf7]/55">
            <span>© {new Date().getFullYear()} Big Bear Studios</span>
            <span>Bengaluru / Mumbai</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
